import { useMemo, useState } from "react";
import { ChangeAlertCallbackType } from "../../../Root";
import { useAFBFunction } from "./useAFBFunction";
import { Box, Divider, IconButton, Switch } from "@mui/material";
import { useDialog, DialogProps } from "@albo-ui/core";
import { Edit } from "@mui/icons-material";
import { afbRecurringFrecuency } from "../../../../util/DataObject";
import moment from "moment";
moment.locale("es");

export const useAFBRecurrentAdmin = (changeAlert: ChangeAlertCallbackType) => {
    const { handleRecurringList, handleRecurringSave, handleRecurringUpdate } = useAFBFunction(changeAlert);
    const dialog = useDialog();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState(null);
    const [info, setInfo] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalDocuments, setTotalDocuments] = useState(0);

    const agent = localStorage.session ? JSON.parse(localStorage.session) : {};

    const onOpenDialog = (info) => {
        setInfo(info);
        dialog.openDialog();
    };
    
    const onCloseDialog: DialogProps["onClose"] = () => {
        setInfo(null);
        dialog.onClose();
    };

    const saveData = async (data, config?) => {
        let validate = Array.isArray(data?.content);
        setPage(data.pagination["page_number"] - 1 || 0);
        setData(JSON.parse(JSON.stringify(validate ? data.content || [] : [data.content])));
        setTotalDocuments(data.pagination["total"] || 0);
        config && setConfig(JSON.parse(JSON.stringify(config)));
    };

    const getData = async (newPage = page, newPerPage = rowsPerPage, filter?) => {
        setLoading(true);
        let payload = {
            data: {
                filter: {},
                pagination: {
                    page_number: newPage,
                    results_per_page: newPerPage
                }
            }
        };

        filter && (payload.data.filter = filter);

        let resp = await handleRecurringList(payload);
        resp ? saveData(resp) : setData([]);
        setLoading(false);
    };

    const onHandleCreate = async (payload) => {
        setLoading(true);
        let resp = await handleRecurringSave(payload);
        if (resp) {
            changeAlert(true, "success", "El registro se creó de forma exitosa.");
            onCloseDialog();
            return changePage(null, 0);
        }
        setLoading(false);
    };

    const onHandleStatus = (row) => {
        const payload = {
            data: {
                uuid: row.uuid, 
                status: row.status === "enabled" ? "disabled" : "enabled",
                start_date: row.start_date,
                end_date: row.end_date,
                updated_by: agent['email']
            }
        };

        return onHandleUpdate(payload);
    };

    const onHandleUpdate = async (payload) => {
        setLoading(true);
        let resp = await handleRecurringUpdate(payload);
        if (resp) {
            changeAlert(true, "success", "El registro se actualizó de forma exitosa.");
            onCloseDialog();
            return changePage(null, 0);
        }
        setLoading(false);
    };

    const type = {
        TOTAL: 'Total', FIXED: 'Fija', PERCENTAGE: 'Porcentaje'
    };

    const onDisplayItem = (row) => {
        const props = [
            { key: 'next_execution_date', label: 'Próxima fecha de ejecución', date: true },
            { key: 'start_date', label: 'Fecha de inicio', date: true },
            { key: 'end_date', label: 'Fecha de fin', date: true, space: true },
            { key: 'successful_executions', label: 'Ejecuciones exitosas' },
            { key: 'failed_executions', label: 'Ejecuciones fallidas' },
            { key: 'total_executions', label: 'Ejecuciones totales', space: true },
            { key: ['amount_config', 'type'], label: 'Tipo', type: type[row['data']['amount_config']['type']] },
            { key: ['amount_config', 'value'], label: 'Monto', space: true },
            { key: 'notification_emails', label: 'Correos de notificación', array: true },
            { key: 'created_by', label: 'Creado por' },
            { key: 'updated_by', label: 'Actualizado por' },
        ];

        return (
            <Box className="dataBlock" ml={"4rem"}>
                {props.map((i) => {
                    const key = Array.isArray(i.key) ? row['data'][i.key[0]][i.key[1]] : row['data'][i.key];
                    
                    return key && <div key={Array.isArray(i.key) ? `${i.key[0]}-${i.key[1]}` : i.key}>
                    <div className="report_display">
                        <label className="neutral" style={{ width: "28%" }}>{i.label}</label>
                        {i['type'] ? <p>{i['type']}</p> : 
                            i['array'] ? <ul style={{ listStyleType: "none" }}>{key.map((a) => <p>{a}</p>)}</ul>
                            : <p>{i.date ? moment(new Date(key)).format("LLL") : key || "N/A"}</p>
                        }
                    </div>
                    {i.space && <Divider sx={{my: 1, width: "50%", "&::before, &::after": {borderColor: "#e3e7eb"}}} />}
                    </div>
                })}
            </Box>
        )
    };

    const onHandleSearch = (payload) => {
        const filter = {
            customer_uuid: payload.value
        };
        getData(1, rowsPerPage, filter);
    };

    const changePage = (action, page, rows = rowsPerPage) => {
        setPage(page);
        getData(page + 1, rows);
    };
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(JSON.parse(JSON.stringify(event.target.value)));
        changePage(null, 0, parseInt(event.target.value, 10));
    };

    const calculateColumns = useMemo(() => {
        const columns = [
            { selector: (row) => (row['metadata'] && row['metadata']['customer_name']) || "Por definir", name: 'Empresa', wrap: true, sortable: true,  },
            { selector: (row) => row['source_customer_uuid'], name: 'Customer UUID', wrap: true, sortable: true, width: "15%" },
            { selector: (row) => row['source_space_uuid'], name: 'Space Origen', wrap: true, sortable: true, width: "15%" },
            { selector: (row) => row['target_receipt']['name'], name: 'Cuenta Destino', wrap: true, sortable: true,  },
            { selector: (row) => row['target_receipt']['clabe'], name: 'CLABE Destino', wrap: true, sortable: true,  },
            { selector: (row) => afbRecurringFrecuency.filter((item) => row['frecuency'] === item.key)[0].label || "N/A", name: 'Frecuencia', wrap: true, sortable: true, width: "8%" },
            { selector: (row) => moment(new Date(row['cdate'])).format("L"), name: 'Creación', wrap: true, sortable: true, width: "8%" },
            { cell: (row) => <Switch
                    onChange={onHandleStatus.bind(this, row)}
                    defaultChecked={row.status === 'enabled' ? true : false}
                />, name: 'Estatus', sortable: true, width: '9%'
            },
            { cell: (row) => {
                    return (
                        <div style={{ display: "flex" }}>
                            <IconButton title="Editar" onClick={onOpenDialog.bind(this, row)} >
                                <Edit />
                            </IconButton>         
                        </div>
                    );
                }, sortable: true, width: "6%"
            }
        ];

        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        data,
        info,
        loading,
        columns: calculateColumns,
        pagination: { page, rowsPerPage, totalDocuments },
        dialog,
        config,
        saveData,
        onHandleSearch,
        onHandleCreate,
        onHandleUpdate,
        onOpenDialog,
        onCloseDialog,
        onDisplayItem,
        changePage,
        handleChangeRowsPerPage,
    };
};
